import {
	ActiveTasksForTasksTabResponse,
	CreateManualTaskDto,
	CreateManualTaskResponse,
	DeleteTaskResponse,
	PaginationParams,
	TaskUser,
	UpdateTaskDto,
	UpdateTaskResponse,
} from "@dune/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { privateAxios } from "../axios";
import { propositionsQueryKeys } from "../query-keys/propositions.query-keys";
import { societiesQueryKeys } from "../query-keys/societies.query-keys";
import { tasksQueryKeys } from "../query-keys/tasks.query-keys";
import { usersQueryKeys } from "../query-keys/users.query-keys";
import { contractsKeys } from "./contracts.queries";
import { gfasQueryKeys } from "../query-keys/gfas.query-keys";

export const updateTask = async (
	taskId: number,
	updateData: UpdateTaskDto,
): Promise<UpdateTaskResponse> => {
	const { data } = await privateAxios.patch<UpdateTaskResponse>(
		`/tasks/${taskId}`,
		updateData,
	);
	return data;
};

export const createManualTask = async (
	createManualTaskDto: CreateManualTaskDto,
): Promise<CreateManualTaskResponse> => {
	const { data } = await privateAxios.post<CreateManualTaskResponse>(
		"/tasks/manual",
		createManualTaskDto,
	);
	return data;
};

export const deleteTask = async (
	taskId: number,
): Promise<DeleteTaskResponse> => {
	const { data } = await privateAxios.delete<DeleteTaskResponse>(
		`/tasks/${taskId}`,
	);
	return data;
};

export const detachDocumentFromTask = async (
	taskId: number,
	fileId: string,
): Promise<{ message: string }> => {
	const { data } = await privateAxios.delete<{ message: string }>(
		`/tasks/${taskId}/files/${fileId}`,
	);
	return data;
};

export const useUpdateTask = (brokerId?: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			taskId,
			updateData,
		}: { taskId: number; updateData: UpdateTaskDto }) =>
			updateTask(taskId, updateData),
		onSuccess: () => {
			if (brokerId) {
				queryClient.invalidateQueries({
					queryKey: societiesQueryKeys.brokerTasks(brokerId),
					refetchType: "active",
				});
			}
			toast.success("Tâche mise à jour avec succès");
		},
		onError: (error: AxiosError<{ message: string; statusCode: number }>) => {
			console.error("Update task error:", error);

			let errorMessage = "Erreur lors de la mise à jour de la tâche";

			if (error.response?.status === 403) {
				errorMessage = "Vous n'avez pas l'autorisation de modifier cette tâche";
			} else if (error.response?.status === 401) {
				errorMessage = "Non autorisé : veuillez vous reconnecter";
			} else if (error.response?.status === 404) {
				errorMessage = "Tâche introuvable";
			} else if (error.response?.data?.message) {
				errorMessage = error.response.data.message;
			} else if (error.message) {
				errorMessage = error.message;
			}

			toast.error(errorMessage);
		},
	});
};

export const useCreateManualTask = (brokerId?: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (createManualTaskDto: CreateManualTaskDto) =>
			createManualTask(createManualTaskDto),
		onSuccess: () => {
			if (brokerId) {
				queryClient.invalidateQueries({
					queryKey: societiesQueryKeys.brokerTasks(brokerId),
					refetchType: "active",
				});
			}
			toast.success("Tâche créée avec succès");
		},
		onError: (error: AxiosError<{ message: string; statusCode: number }>) => {
			console.error("Create manual task error:", error);

			let errorMessage = "Erreur lors de la création de la tâche";

			if (error.response?.status === 403) {
				errorMessage = "Vous n'avez pas l'autorisation de créer une tâche";
			} else if (error.response?.status === 401) {
				errorMessage = "Non autorisé : veuillez vous reconnecter";
			} else if (error.response?.data?.message) {
				errorMessage = error.response.data.message;
			} else if (error.message) {
				errorMessage = error.message;
			}

			toast.error(errorMessage);
		},
	});
};

export const useCreateManualTaskForProposition = (propositionId: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (createManualTaskDto: CreateManualTaskDto) =>
			createManualTask(createManualTaskDto),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: propositionsQueryKeys.tasks(propositionId),
				refetchType: "active",
			});
			toast.success("Tâche créée avec succès");
		},
		onError: (error: AxiosError<{ message: string; statusCode: number }>) => {
			console.error("Create manual task error:", error);

			let errorMessage = "Erreur lors de la création de la tâche";

			if (error.response?.status === 403) {
				errorMessage = "Vous n'avez pas l'autorisation de créer une tâche";
			} else if (error.response?.status === 401) {
				errorMessage = "Non autorisé : veuillez vous reconnecter";
			} else if (error.response?.data?.message) {
				errorMessage = error.response.data.message;
			} else if (error.message) {
				errorMessage = error.message;
			}

			toast.error(errorMessage);
		},
	});
};

export const useUpdateTaskForProposition = (propositionId: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			taskId,
			updateData,
		}: { taskId: number; updateData: UpdateTaskDto }) =>
			updateTask(taskId, updateData),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: propositionsQueryKeys.tasks(propositionId),
				refetchType: "active",
			});
			toast.success("Tâche mise à jour avec succès");
		},
		onError: (error: AxiosError<{ message: string; statusCode: number }>) => {
			console.error("Update task error:", error);

			let errorMessage = "Erreur lors de la mise à jour de la tâche";

			if (error.response?.status === 403) {
				errorMessage = "Vous n'avez pas l'autorisation de modifier cette tâche";
			} else if (error.response?.status === 401) {
				errorMessage = "Non autorisé : veuillez vous reconnecter";
			} else if (error.response?.status === 404) {
				errorMessage = "Tâche introuvable";
			} else if (error.response?.data?.message) {
				errorMessage = error.response.data.message;
			} else if (error.message) {
				errorMessage = error.message;
			}

			toast.error(errorMessage);
		},
	});
};

export const useUpdateTaskForContract = (contractId: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			taskId,
			updateData,
		}: { taskId: number; updateData: UpdateTaskDto }) =>
			updateTask(taskId, updateData),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: contractsKeys.tasks(contractId),
				refetchType: "active",
			});
			toast.success("Tâche mise à jour avec succès");
		},
		onError: (error: AxiosError<{ message: string; statusCode: number }>) => {
			console.error("Update task error:", error);

			let errorMessage = "Erreur lors de la mise à jour de la tâche";

			if (error.response?.status === 403) {
				errorMessage = "Vous n'avez pas l'autorisation de modifier cette tâche";
			} else if (error.response?.status === 401) {
				errorMessage = "Non autorisé : veuillez vous reconnecter";
			} else if (error.response?.status === 404) {
				errorMessage = "Tâche introuvable";
			} else if (error.response?.data?.message) {
				errorMessage = error.response.data.message;
			} else if (error.message) {
				errorMessage = error.message;
			}

			toast.error(errorMessage);
		},
	});
};

export const useDeleteTask = (brokerId?: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (taskId: number) => deleteTask(taskId),
		onSuccess: () => {
			if (brokerId) {
				queryClient.invalidateQueries({
					queryKey: societiesQueryKeys.brokerTasks(brokerId),
					refetchType: "active",
				});
			}
			toast.success("Tâche supprimée avec succès");
		},
		onError: (error: AxiosError<{ message: string; statusCode: number }>) => {
			console.error("Delete task error:", error);

			let errorMessage = "Erreur lors de la suppression de la tâche";

			if (error.response?.status === 403) {
				errorMessage =
					"Vous n'avez pas l'autorisation de supprimer cette tâche";
			} else if (error.response?.status === 401) {
				errorMessage = "Non autorisé : veuillez vous reconnecter";
			} else if (error.response?.status === 404) {
				errorMessage = "Tâche introuvable";
			} else if (error.response?.data?.message) {
				errorMessage = error.response.data.message;
			} else if (error.message) {
				errorMessage = error.message;
			}

			toast.error(errorMessage);
		},
	});
};

export const useUpdateTaskForConstructionSite = (
	constructionSiteId: string,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			taskId,
			updateData,
		}: { taskId: number; updateData: UpdateTaskDto }) =>
			updateTask(taskId, updateData),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["construction-sites", "tasks", constructionSiteId],
				refetchType: "active",
			});
			toast.success("Tâche mise à jour avec succès");
		},
		onError: (error: AxiosError<{ message: string; statusCode: number }>) => {
			console.error("Update task error:", error);

			let errorMessage = "Erreur lors de la mise à jour de la tâche";

			if (error.response?.status === 403) {
				errorMessage = "Vous n'avez pas l'autorisation de modifier cette tâche";
			} else if (error.response?.status === 401) {
				errorMessage = "Non autorisé : veuillez vous reconnecter";
			} else if (error.response?.status === 404) {
				errorMessage = "Tâche introuvable";
			} else if (error.response?.data?.message) {
				errorMessage = error.response.data.message;
			} else if (error.message) {
				errorMessage = error.message;
			}

			toast.error(errorMessage);
		},
	});
};

export const useDeleteTaskForProposition = (propositionId: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (taskId: number) => deleteTask(taskId),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: propositionsQueryKeys.tasks(propositionId),
				refetchType: "active",
			});
			toast.success("Tâche supprimée avec succès");
		},
		onError: (error: AxiosError<{ message: string; statusCode: number }>) => {
			console.error("Delete task error:", error);

			let errorMessage = "Erreur lors de la suppression de la tâche";

			if (error.response?.status === 403) {
				errorMessage =
					"Vous n'avez pas l'autorisation de supprimer cette tâche";
			} else if (error.response?.status === 401) {
				errorMessage = "Non autorisé : veuillez vous reconnecter";
			} else if (error.response?.status === 404) {
				errorMessage = "Tâche introuvable";
			} else if (error.response?.data?.message) {
				errorMessage = error.response.data.message;
			} else if (error.message) {
				errorMessage = error.message;
			}

			toast.error(errorMessage);
		},
	});
};

export const useDeleteTaskForContract = (contractId: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (taskId: number) => deleteTask(taskId),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: contractsKeys.tasks(contractId),
				refetchType: "active",
			});
			toast.success("Tâche supprimée avec succès");
		},
		onError: (error: AxiosError<{ message: string; statusCode: number }>) => {
			console.error("Delete task error:", error);

			let errorMessage = "Erreur lors de la suppression de la tâche";

			if (error.response?.status === 403) {
				errorMessage =
					"Vous n'avez pas l'autorisation de supprimer cette tâche";
			} else if (error.response?.status === 401) {
				errorMessage = "Non autorisé : veuillez vous reconnecter";
			} else if (error.response?.status === 404) {
				errorMessage = "Tâche introuvable";
			} else if (error.response?.data?.message) {
				errorMessage = error.response.data.message;
			} else if (error.message) {
				errorMessage = error.message;
			}

			toast.error(errorMessage);
		},
	});
};

export const useUpdateTaskForGFA = (gfaId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			taskId,
			updateData,
		}: { taskId: number; updateData: UpdateTaskDto }) =>
			updateTask(taskId, updateData),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: gfasQueryKeys.tasks(gfaId),
				refetchType: "active",
			});
			toast.success("Tâche mise à jour avec succès");
		},
		onError: (error: AxiosError<{ message: string; statusCode: number }>) => {
			console.error("Update task error:", error);

			let errorMessage = "Erreur lors de la mise à jour de la tâche";

			if (error.response?.status === 403) {
				errorMessage = "Vous n'avez pas l'autorisation de modifier cette tâche";
			} else if (error.response?.status === 401) {
				errorMessage = "Non autorisé : veuillez vous reconnecter";
			} else if (error.response?.status === 404) {
				errorMessage = "Tâche introuvable";
			} else if (error.response?.data?.message) {
				errorMessage = error.response.data.message;
			} else if (error.message) {
				errorMessage = error.message;
			}

			toast.error(errorMessage);
		},
	});
};

export const useDeleteTaskForGFA = (gfaId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (taskId: number) => deleteTask(taskId),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: gfasQueryKeys.tasks(gfaId),
				refetchType: "active",
			});
			toast.success("Tâche supprimée avec succès");
		},
		onError: (error: AxiosError<{ message: string; statusCode: number }>) => {
			console.error("Delete task error:", error);

			let errorMessage = "Erreur lors de la suppression de la tâche";

			if (error.response?.status === 403) {
				errorMessage =
					"Vous n'avez pas l'autorisation de supprimer cette tâche";
			} else if (error.response?.status === 401) {
				errorMessage = "Non autorisé : veuillez vous reconnecter";
			} else if (error.response?.status === 404) {
				errorMessage = "Tâche introuvable";
			} else if (error.response?.data?.message) {
				errorMessage = error.response.data.message;
			} else if (error.message) {
				errorMessage = error.message;
			}

			toast.error(errorMessage);
		},
	});
};

export const useDetachDocumentFromTask = (brokerId?: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ taskId, fileId }: { taskId: number; fileId: string }) =>
			detachDocumentFromTask(taskId, fileId),
		onSuccess: () => {
			if (brokerId) {
				queryClient.invalidateQueries({
					queryKey: societiesQueryKeys.brokerTasks(brokerId),
					refetchType: "active",
				});
			}
			toast.success("Document dissocié de la tâche avec succès");
		},
		onError: (error: AxiosError<{ message: string; statusCode: number }>) => {
			console.error("Detach document from task error:", error);

			let errorMessage = "Erreur lors de la dissociation du document";

			if (error.response?.status === 403) {
				errorMessage = "Vous n'avez pas l'autorisation de modifier cette tâche";
			} else if (error.response?.status === 401) {
				errorMessage = "Non autorisé : veuillez vous reconnecter";
			} else if (error.response?.status === 404) {
				errorMessage = "Tâche ou document introuvable";
			} else if (error.response?.data?.message) {
				errorMessage = error.response.data.message;
			} else if (error.message) {
				errorMessage = error.message;
			}

			toast.error(errorMessage);
		},
	});
};

export const getAssignableUsers = async (): Promise<TaskUser[]> => {
	const { data } = await privateAxios.get<TaskUser[]>("/users/assignable");
	return data;
};

export const useGetAssignableUsers = () => {
	return useQuery({
		queryKey: usersQueryKeys.assignable(),
		queryFn: getAssignableUsers,
	});
};

const getAllTasksForTasksTabPaginated = async (
	params: PaginationParams,
): Promise<ActiveTasksForTasksTabResponse> => {
	const { data } = await privateAxios.get<ActiveTasksForTasksTabResponse>(
		"/tasks",
		{
			params: { page: params.page, limit: params.limit },
		},
	);
	return data;
};

export const useGetAllActiveTasks = (params: PaginationParams) => {
	return useQuery({
		queryKey: tasksQueryKeys.activeTasks(params),
		queryFn: () => getAllTasksForTasksTabPaginated(params),
	});
};
